import { Button } from '@mantine/core';
import { Spotlight, createSpotlight } from '@mantine/spotlight';
import { Pencil, Search } from 'lucide-react';
import { FC, ReactElement, useMemo, useState } from 'react';

import { CustomSpotlight } from '@/components/custom-spotlight.tsx';

import { EnrichedCustomer, EnrichedLocation } from '@/models/EnrichedPrismicDocuments.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';
import { useConfigStore } from '@/stores/configStore.ts';

import Icon from './Icon.tsx';
import Paragraph from './text/Paragraph.tsx';

const LIMIT = 5;

const [spotlight_store, spotlight_utils] = createSpotlight();

// TODO: add recommendation logic
const CustomerPracticeSelection: FC = () => {
    const [query, setQuery] = useState<string>('');
    const { selected_customer, selected_location, user_customers: customers } = useConfigStore((state) => state);
    const isSeriesPlacementMode = useBookingStore((state) => state.isSeriesPlacementMode);

    // Function to handle location change and related state updates
    const handleLocationChange = (customer: EnrichedCustomer, location: EnrichedLocation): void => {
        // Update config store first
        useConfigStore.setState({
            selected_customer: customer,
            selected_location: location,
        });
    };

    const limitOptions = (options: ReactElement[]): ReactElement[] => {
        return options.slice(0, LIMIT);
    };

    const filtered_results: ReactElement[] = useMemo(() => {
        if (!query) {
            return limitOptions(
                customers.map((customer: EnrichedCustomer) => (
                    <Spotlight.ActionsGroup key={customer.key} label={customer.customer_name}>
                        {customer.locations.map((location: EnrichedLocation) => (
                            <Spotlight.Action
                                onClick={() => handleLocationChange(customer, location)}
                                key={`${customer.key}_${location.key}`}
                                label={location.title}
                            />
                        ))}
                    </Spotlight.ActionsGroup>
                ))
            );
        }

        const formatted_query = query.toLowerCase().trim();
        const filtered_options: ReactElement[] = [];

        for (const customer of customers) {
            const is_customer_name_match = customer.customer_name.toLowerCase().trim().includes(formatted_query);
            const filtered_locations = customer.locations.filter((location) =>
                location.title.toLowerCase().trim().includes(formatted_query)
            );

            if (!is_customer_name_match && filtered_locations.length === 0) {
                continue;
            }

            filtered_options.push(
                <Spotlight.ActionsGroup key={customer.key} label={customer.customer_name}>
                    {(is_customer_name_match ? customer.locations : filtered_locations).map(
                        (location: EnrichedLocation) => (
                            <Spotlight.Action
                                onClick={() => handleLocationChange(customer, location)}
                                key={`${customer.key}_${location.key}`}
                                label={location.title}
                            />
                        )
                    )}
                </Spotlight.ActionsGroup>
            );
        }

        return limitOptions(filtered_options);
    }, [query, customers]);

    return (
        <>
            <Button
                className="border-slate-300 dark:text-neutral-300 dark:hover:text-neutral-200"
                variant="outline"
                onClick={spotlight_utils.open}
                disabled={isSeriesPlacementMode}
                rightSection={<Icon icon={Pencil} />}
            >
                {selected_customer && selected_location ? (
                    <div className="align-center color flex items-center gap-2">
                        <Paragraph>{selected_location.title}</Paragraph>
                    </div>
                ) : (
                    'Keine Praxis ausgewählt'
                )}
            </Button>
            <CustomSpotlight
                id="customer-practice-spotlight"
                query={query}
                setQuery={setQuery}
                spotlightStore={spotlight_store}
                spotLightUtils={spotlight_utils}
            >
                <Spotlight.Search placeholder="Suche..." leftSection={<Search className="size-6" />} />
                <div className="max-h-[50vh] overflow-y-auto">
                    <Spotlight.ActionsList>
                        {filtered_results.length > 0 ? (
                            filtered_results
                        ) : (
                            <Spotlight.Empty>Für diese Suche wurden keine Ergebnisse gefunden!</Spotlight.Empty>
                        )}
                    </Spotlight.ActionsList>
                </div>
            </CustomSpotlight>
        </>
    );
};

export default CustomerPracticeSelection;
